<script setup lang="ts">
// Shown on every page while a platform admin is acting as another user.
const { t } = useI18n()
const authStore = useAuthStore()
const session = useSession()
const toast = useToast()

const now = ref(Date.now())
const stopping = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

// The automatic stop fires at most once per assume session; the Stop button
// stays available for manual retries.
let autoStopTried = false

// An unparseable expiry counts as already expired.
const msLeft = computed(() => {
  if (!authStore.assumedBy) return 0
  const left = new Date(authStore.assumedBy.expires_at).getTime() - now.value
  return Number.isFinite(left) ? left : 0
})
const minutesLeft = computed(() => Math.max(0, Math.ceil(msLeft.value / 60_000)))

async function stop() {
  if (stopping.value) return
  stopping.value = true
  try {
    await session.stopAssume()
  } catch {
    toast.add({ title: t('assume.stopFailed'), color: 'error' })
  } finally {
    stopping.value = false
  }
}

watch(() => authStore.assumedBy?.expires_at, () => {
  autoStopTried = false
})

function tick() {
  now.value = Date.now()
  if (authStore.assumedBy && msLeft.value <= 0 && !autoStopTried) {
    autoStopTried = true
    void stop()
  }
}

function stopTimer() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

// Tick only while a session exists, so ordinary visitors run no timer.
watch(() => !!authStore.assumedBy, (assumed) => {
  stopTimer()
  if (!assumed || !import.meta.client) return
  tick()
  timer = setInterval(tick, 1000)
}, { immediate: true })

onBeforeUnmount(stopTimer)
</script>

<template>
  <!-- Sits directly above the mobile tab bar, whose measured height
       MobileTabBar publishes as a CSS variable (0 when there is no bar).
       The safe-area padding applies only when the banner is at the screen
       edge, i.e. when the bar is not covering the inset already. -->
  <div
    v-if="authStore.assumedBy"
    data-testid="assume-banner"
    role="status"
    class="fixed bottom-[var(--mobile-tab-bar-height,0px)] inset-x-0 z-[60] flex flex-wrap items-center justify-center gap-x-4 gap-y-1 bg-warning px-4 pt-2 pb-[max(0.5rem,calc(env(safe-area-inset-bottom)_-_var(--mobile-tab-bar-height,0px)))] text-sm font-medium text-inverted shadow-lg"
  >
    <!-- Two groups, so a narrow screen wraps between them and never strands
         the icon or the Stop button on a line of its own. -->
    <span class="flex min-w-0 items-center gap-2">
      <UIcon
        name="i-lucide-user-cog"
        class="size-4 shrink-0"
      />
      <span class="break-all">{{ t('assume.banner', { email: authStore.currentActor.email }) }}</span>
    </span>
    <span class="flex shrink-0 items-center gap-3">
      <span>{{ t('assume.minutesLeft', { n: minutesLeft }) }}</span>
      <UButton
        data-testid="assume-stop-btn"
        :label="t('assume.stop')"
        :loading="stopping"
        size="xs"
        color="neutral"
        variant="solid"
        @click="stop"
      />
    </span>
  </div>
  <!-- In-flow spacer: lets the end of the page scroll clear of the fixed banner. -->
  <div
    v-if="authStore.assumedBy"
    aria-hidden="true"
    class="h-24 md:h-12"
  />
</template>
